import React from 'react';
import styled from 'styled-components';
import { formatPhoneNumber, isValidPhone, ValidationMessages } from '../../utils/validation';

interface ThemeProps {
  $night?: boolean;
}

const InputWrapper = styled.div`
  width: 100%;
`;

const Input = styled.input<ThemeProps>`
  width: 100%;
  padding: 12px 15px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.borderMuted};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Error = styled.span`
  color: #f44336;
  font-size: 0.75rem;
  margin-top: 4px;
  display: block;
`;

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean, error?: string) => void;
  placeholder?: string;
  $night?: boolean;
  className?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onValidationChange,
  placeholder = '(555) 123-4567',
  $night,
  className
}) => {
  const [error, setError] = React.useState<string | undefined>();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Допустимые символы: цифры, пробелы, (), -, +
    const isValidChar = /^[0-9\s()\-+]*$/.test(inputValue);

    if (isValidChar) {
      const formatted = formatPhoneNumber(inputValue);
      onChange(formatted);
      // Сбрасываем ошибку при вводе
      if (error) {
        setError(undefined);
        onValidationChange?.(true);
      }
    }
  };

  const handleBlur = () => {
    if (value && !isValidPhone(value)) {
      setError(ValidationMessages.invalidPhone);
      onValidationChange?.(false, ValidationMessages.invalidPhone);
    } else {
      setError(undefined);
      onValidationChange?.(true);
    }
  };

  return (
    <InputWrapper className={className}>
      <Input
        $night={$night}
        type="tel"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        maxLength={17}
        inputMode="tel"
      />
      {error && <Error>{error}</Error>}
    </InputWrapper>
  );
};

export default PhoneInput;
